/**
 * VergleichEmpfehlungPanel — the recommendation editor + the FIDLEG Freigabe + the Word-Export actions
 * (P2 Placement Slice 11, Tab 4b).
 */

import { useState } from 'react';
import {
  OFFER_EMPFEHLUNG_BEGRUENDUNG_MIN,
  VERGLEICH_ABWEICHEND_LABEL,
  VERGLEICH_BEGRUENDUNG_HINWEIS,
  VERGLEICH_FREIGABE_HINWEIS,
  VERGLEICH_FREIGEGEBEN_HINWEIS,
  VERGLEICH_GENERIERUNG_FEHLER_HINWEIS,
  VERGLEICH_GENERIERUNG_LAEUFT_HINWEIS,
  VERGLEICH_GEWICHTE_SUMME_HINWEIS,
  VERGLEICH_KI_HINWEIS,
  VERGLEICH_VERALTET_HINWEIS,
  type ComparisonReportEintrag,
  type OfferComparisonPatchBody,
} from '@brokly/shared';
import { apiClient } from '../../lib/api-client';
import { dateTimeShortDe } from '../../lib/format';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Dialog } from '../administration/components';

function mapError(error: string | null): string | null {
  if (!error) return null;
  switch (error) {
    case 'empfehlung_unvollstaendig':
      return VERGLEICH_BEGRUENDUNG_HINWEIS;
    case 'report_veraltet':
      return VERGLEICH_VERALTET_HINWEIS;
    case 'generierung_laeuft':
      return VERGLEICH_GENERIERUNG_LAEUFT_HINWEIS;
    case 'report_freigegeben':
      return VERGLEICH_FREIGEGEBEN_HINWEIS;
    case 'gewichte_summe':
      return VERGLEICH_GEWICHTE_SUMME_HINWEIS;
    default:
      return error;
  }
}

export function VergleichEmpfehlungPanel({
  placementId,
  bericht,
  canEdit,
  canRelease,
  onPatch,
  onFreigeben,
  onExport,
  onKiNeu,
  saving,
  exporting,
  releasing,
  error,
}: {
  placementId: string;
  bericht: ComparisonReportEintrag;
  canEdit: boolean;
  canRelease: boolean;
  onPatch: (patch: OfferComparisonPatchBody) => void;
  onFreigeben: () => void;
  onExport: () => void;
  onKiNeu: () => void;
  saving: boolean;
  exporting: boolean;
  releasing: boolean;
  error: string | null;
}) {
  const released = bericht.status === 'freigegeben';
  const [empfehlung, setEmpfehlung] = useState(bericht.empfehlung_offer_id ?? '');
  const [begruendung, setBegruendung] = useState(bericht.empfehlung_begruendung ?? '');
  const [zusammenfassung, setZusammenfassung] = useState(bericht.effektive_zusammenfassung ?? '');
  const [confirmOpen, setConfirmOpen] = useState(false);

  const begruendungOk = begruendung.trim().length >= OFFER_EMPFEHLUNG_BEGRUENDUNG_MIN;
  const chosen = bericht.offerten.find((o) => o.offer_document_id === empfehlung);

  const speichern = (): void => {
    onPatch({
      empfehlung_offer_id: empfehlung || undefined,
      empfehlung_begruendung: begruendung.trim() === '' ? null : begruendung,
      zusammenfassung: zusammenfassung.trim() === '' ? null : zusammenfassung,
    });
  };

  const shownError = mapError(error);

  return (
    <div className="rounded-lg border border-border p-3" data-testid="vergleich-empfehlung-panel">
      <h4 className="font-medium">Empfehlung</h4>

      <label className="mt-2 flex flex-col gap-1 text-sm">
        <span className="text-muted-foreground">Empfohlene Offerte</span>
        <select
          className="rounded-md border border-border p-2"
          value={empfehlung}
          disabled={!canEdit}
          onChange={(e) => setEmpfehlung(e.target.value)}
          data-testid="vergleich-empfehlung-select"
        >
          {bericht.offerten.map((o) => (
            <option key={o.offer_document_id} value={o.offer_document_id}>
              {o.rang}. {o.carrier_name} - Score {o.score_total}
            </option>
          ))}
        </select>
      </label>
      {bericht.empfehlung_abweichend && <Badge variant="warning">{VERGLEICH_ABWEICHEND_LABEL}</Badge>}

      <label className="mt-2 flex flex-col gap-1 text-sm">
        <span className="text-muted-foreground">Begruendung</span>
        <textarea className="rounded-md border border-border p-2" rows={2} value={begruendung} disabled={!canEdit} onChange={(e) => setBegruendung(e.target.value)} data-testid="vergleich-begruendung" />
      </label>
      {canEdit && !begruendungOk && <p className="text-xs text-muted-foreground">{VERGLEICH_BEGRUENDUNG_HINWEIS}</p>}

      <label className="mt-2 flex flex-col gap-1 text-sm">
        <span className="text-muted-foreground">Zusammenfassung</span>
        <textarea className="rounded-md border border-border p-2" rows={3} value={zusammenfassung} disabled={!canEdit} onChange={(e) => setZusammenfassung(e.target.value)} />
      </label>
      {canEdit && (
        <div className="mt-1 flex gap-2">
          <Button size="sm" disabled={saving} onClick={speichern} data-testid="vergleich-empfehlung-speichern">Speichern</Button>
          <Button variant="ghost" size="sm" disabled={saving} onClick={() => onPatch({ zusammenfassung: null })}>KI-Text uebernehmen</Button>
        </div>
      )}

      <div className="mt-3 text-sm" data-testid="vergleich-status-zeile">
        {released ? (
          <span className="text-muted-foreground">
            {bericht.status_label} - {bericht.freigegeben_by_name ?? '-'}, {dateTimeShortDe(bericht.freigegeben_at)}
          </span>
        ) : bericht.generation_status === 'laufend' ? (
          <span className="text-muted-foreground" data-testid="vergleich-ki-laeuft">⌛ {VERGLEICH_GENERIERUNG_LAEUFT_HINWEIS}</span>
        ) : bericht.generation_status === 'fehlgeschlagen' ? (
          <span className="flex flex-wrap items-center gap-2" data-testid="vergleich-ki-fehler">
            <span className="text-[var(--status-danger-text,#b42318)]">{VERGLEICH_GENERIERUNG_FEHLER_HINWEIS}</span>
            <Button variant="outline" size="sm" onClick={onKiNeu}>KI-Bewertung erneut erzeugen</Button>
          </span>
        ) : (
          <span className="text-muted-foreground">{bericht.generation_status_label}</span>
        )}
      </div>

      <div role="note" className="mt-2 rounded-md border border-border bg-muted p-2 text-xs text-muted-foreground">{VERGLEICH_KI_HINWEIS}</div>

      {shownError && <p className="mt-2 text-sm text-[var(--status-danger-text,#b42318)]" data-testid="vergleich-panel-error">{shownError}</p>}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" disabled={exporting} onClick={onExport} data-testid="vergleich-export">Word-Export</Button>
        {bericht.docx.veraltet && bericht.docx.vorhanden && <Badge variant="warning">Export veraltet</Badge>}
        {bericht.docx.vorhanden && (
          <a className="text-sm text-primary underline" href={apiClient.placements.vergleichDateiUrl(placementId, bericht.id)} download data-testid="vergleich-datei-link">
            {bericht.docx.filename ?? 'Word-Datei'}
          </a>
        )}
        {!released && (
          <Button size="sm" disabled={!canRelease || !begruendungOk || releasing} onClick={() => setConfirmOpen(true)} data-testid="vergleich-freigeben">
            Empfehlung freigeben
          </Button>
        )}
      </div>

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Empfehlung freigeben"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>Abbrechen</Button>
            <Button
              disabled={releasing}
              onClick={() => {
                setConfirmOpen(false);
                onFreigeben();
              }}
              data-testid="vergleich-freigeben-bestaetigen"
            >
              Freigeben
            </Button>
          </div>
        }
      >
        <p className="text-sm">
          {VERGLEICH_FREIGABE_HINWEIS}
          {chosen && <span className="mt-2 block font-medium">{chosen.carrier_name} ({chosen.submission_number})</span>}
        </p>
      </Dialog>
    </div>
  );
}
